import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  split,
} from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import LoadingView from './atoms/LoadingView';

const HASURA_URL = import.meta.env.VITE_HASURA_URL;
const HASURA_WS_URL = import.meta.env.VITE_HASURA_WS_URL;
const appDisplayVersion = import.meta.env.VITE_GIT_SHORT_HASH;

// C&P'd from setupSentry.js if needed in a 3rd place, let's just put it in utils
function determineEnvironment() {
  // hostname gives us localhost. host would give us localhost:3000
  const subDomain = window.location.hostname.split('.')[0]; // subodmain OR localhost

  switch (subDomain) {
    case 'localhost':
      return 'local-dev';
    case 'my':
      return 'production';
    case 'dev':
      return 'development';
    default:
      return 'unknown';
  }
}

function getDefaultHasuraRole(auth0User) {
  return auth0User?.['https://feedflo.com/roles']?.includes('researcher') ? 'researcher' : 'user';
}

export default function ApolloWrapper({ children }) {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();
  const [bearerToken, setBearerToken] = useState(null);
  const [apClient, setApClient] = useState(null);
  // subscriptions-transport-ws only uses connectionParams at connect, not the Operation;
  // store the merged headers from auth + hook context for the most recent subscription start.
  const subscriptionConnectionHeadersRef = useRef(null);
  if (!isAuthenticated && !isLoading) {
    loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } });
  }

  useEffect(() => {
    async function checkAuthentication() {
      if (isAuthenticated) {
        const token = isAuthenticated ? await getAccessTokenSilently() : null;
        setBearerToken(token);
      }
    }
    checkAuthentication();
  }, [isAuthenticated]);

  const httpLink = createHttpLink({
    uri: HASURA_URL,
  });

  // first link in the chain
  const authLink = setContext((_, { headers, ...context }) => {
    if (!bearerToken) return { headers };
    return {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
        'x-hasura-role': getDefaultHasuraRole(user),
        ...headers,
      },
      ...context,
    };
  });

  // Runs after authLink: capture merged Hasura/Auth headers for WebSocket connection_init.
  const captureSubscriptionConnectionContextLink = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        const definition = getMainDefinition(operation.query);
        if (definition.kind === 'OperationDefinition' && definition.operation === 'subscription') {
          const { headers: mergedHeaders } = operation.getContext();
          if (mergedHeaders) {
            subscriptionConnectionHeadersRef.current = { ...mergedHeaders };
          }
        }
        return forward(operation); // provides the whole operation to the next link in the chain.
      }),
    [],
  );

  // WebSocket Link (see subscriptionConnectionHeadersRef; last subscription wins if roles differ)
  const wsLink = new WebSocketLink({
    uri: HASURA_WS_URL,
    options: {
      reconnect: true,
      lazy: true,
      connectionParams: () => ({
        headers: subscriptionConnectionHeadersRef.current
          ? { ...subscriptionConnectionHeadersRef.current }
          : {
            Authorization: `Bearer ${bearerToken}`,
            'x-hasura-role': getDefaultHasuraRole(user),
          },
      }),
    },
  });

  // Send query request based on the type definition (all operations through auth, then sub capture or HTTP)
  const linkChain = authLink
    .concat(captureSubscriptionConnectionContextLink)
    .concat(
      split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink,
        httpLink,
      ),
    );

  if (bearerToken && !apClient) {
    const client = new ApolloClient({
      link: linkChain,
      name: `web_${determineEnvironment()}`,
      version: appDisplayVersion || 'unknown',
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              alert_dashboard_table_data: {
                keyArgs: false,
                merge: (existing = {}, incoming, { args }) => {
                  const pageSize = 50; // The max number of rows that can be returned for the Alert Table
                  const merged = existing?.rows && args.page > 0 ? existing.rows.slice() : [];

                  for (let i = 0; i < incoming.rows.length; i++) {
                    merged[args.page * pageSize + i] = incoming.rows[i];
                  }

                  return {
                    ...incoming,
                    rows: merged,
                  };
                },
              },
            },
          },
        },
      }),
      connectToDevTools: true,
    });
    setApClient(client);
  }

  if (apClient) {
    return <ApolloProvider client={apClient}>{children}</ApolloProvider>;
  }
  return (
    <div className="loadingPage">
      <LoadingView />
    </div>
  );
}

ApolloWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};
